import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SectionHeader from 'components/SectionHeader/SectionHeader';
import { Button, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import { UsersContextProvider, useUsersContext } from 'context/users.context';
import { Permission } from '@restore/services-lib';
import { TableColumnInterface } from 'components/Table/table.types';
import OverviewTable from 'components/Table/Table';
import RemovePermission from 'pages/UserGroups/UserGroup/RemovePermission';
import SingleSelectDropdown from 'components/DropdownSelect/SingleSelectDropdown/SingleSelectDropdown';
import UpdateUserGroup from 'pages/UserGroups/UserGroup/UpdateUserGroup';
import { DropdownOption } from 'components/DropdownSelect/dropdown.types';
import DeleteUserGroup from 'pages/UserGroups/DeleteUserGroup';
import PermissionCheck from 'components/Abilities/PermissionCheck';
import { Div } from 'components/Elements/Div/Div';
import LoadingElement from 'components/LoadingElement/LoadingElement';

const ActionsContainer = styled('div')`
  display: flex;
  padding: 0 1rem 1rem 1rem;
  align-items: flex-end;
  margin-top: 1rem;
  width: 100%;
  gap: 0.75rem;

  ${({ theme }) => theme.breakpoints.down('md')} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const UserGroupComponent = () => {
  const { groupId } = useParams();
  const { loadingUserGroups, getUserGroupById, permissions, editUserGroup } = useUsersContext();
  const userGroup = getUserGroupById(Number(groupId));
  const navigate = useNavigate();
  const [selectedPermission, setSelectedPermission] = useState<DropdownOption>();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!loadingUserGroups && !userGroup) {
      navigate('/groups');
    }
  }, [loadingUserGroups, navigate, userGroup]);

  if (!userGroup) {
    return null;
  }

  const selectOptions = () => {
    const selectablePermissions = permissions?.filter(
      (permission) =>
        !userGroup.permissions?.find(
          (userGroupPermission) => permission.id === userGroupPermission.id
        )
    );

    return selectablePermissions?.map((permission) => ({
      name: `${permission.name} - ${permission.scope}`,
      id: permission.id,
    }));
  };

  const handleAddPermission = async () => {
    setLoading(true);
    if (selectedPermission?.id) {
      const newPermissions = [
        selectedPermission.id,
        ...(userGroup.permissions?.map((permission) => permission.id) ?? []),
      ];
      await editUserGroup({ ...userGroup, permissionIds: newPermissions });
      setSelectedPermission(undefined);
    }
    setLoading(false);
  };

  const tableCols: TableColumnInterface<Permission>[] = [
    { label: 'Permission', value: (perm) => perm.name, sortBy: 'name' },
    { label: 'Scope', value: (perm) => perm.scope, sortBy: 'scope' },
    {
      label: 'Actions',
      value: (perm) => <RemovePermission permission={perm} userGroup={userGroup} />,
    },
  ];

  const getPageTitle = () => <>{userGroup.name}</>;

  return (
    <>
      <SectionHeader
        pageHeader
        title={getPageTitle()}
        actions={
          <Div display='flex' gap='0.75rem' alignItems='flex-end'>
            <UpdateUserGroup userGroup={userGroup} />
            <DeleteUserGroup userGroup={userGroup} />
          </Div>
        }
      />
      <PermissionCheck permission={{ action: 'update', subject: 'UserGroup' }}>
        <ActionsContainer>
          <SingleSelectDropdown
            inputProps={{
              sx: { width: '24rem', maxWidth: 'calc(100vw - 1rem)' },
              size: 'small',
            }}
            allOptions={selectOptions()}
            selected={selectedPermission}
            setSelected={(permission) => setSelectedPermission(permission)}
            placeholder='Select a permission'
            containedLabel
          />
          <LoadingElement
            loading={loading}
            element={
              <Button onClick={handleAddPermission} variant='outlined'>
                Add Permission
              </Button>
            }
          />
        </ActionsContainer>
      </PermissionCheck>
      <OverviewTable tableTitle='Permissions' columns={tableCols} data={userGroup.permissions} />
    </>
  );
};

const UserGroup = () => (
  <UsersContextProvider>
    <UserGroupComponent />
  </UsersContextProvider>
);

export default UserGroup;
